import {
  createNestablePublicClientApplication,
  InteractionRequiredAuthError,
  type IPublicClientApplication,
} from '@azure/msal-browser';
import { GRAPH_CLIENT_ID, GRAPH_TENANT_ID } from './config';
import type { EmailDirection, EmailParticipant } from './types';

// Microsoft Graph access for the "Log thread" feature, via Nested App
// Authentication (NAA). Office.js only hands us the single open email; to log a
// whole conversation we read its sibling messages from Graph (Mail.Read). NAA
// brokers a delegated token through the Outlook host - no backend, no extra
// sign-in when there's an active Office session.

const GRAPH_SCOPES = ['Mail.Read'];

let msalInstance: IPublicClientApplication | undefined;

const initMsal = async (): Promise<IPublicClientApplication> => {
  if (!msalInstance) {
    msalInstance = await createNestablePublicClientApplication({
      auth: {
        clientId: GRAPH_CLIENT_ID,
        authority: `https://login.microsoftonline.com/${GRAPH_TENANT_ID}`,
      },
      cache: { cacheLocation: 'localStorage' },
    });
  }
  return msalInstance;
};

// NAA isn't available on older Outlook builds - the UI uses this to hide/disable
// "Log thread" and fall back to single-email logging.
export const isThreadLoggingSupported = (): boolean => {
  try {
    return !!Office.context?.requirements?.isSetSupported('NestedAppAuth', '1.1');
  } catch {
    return false;
  }
};

// loginHint pins MSAL to the CLS mailbox account. Without it, a user with
// several Microsoft accounts can have the interactive popup authenticate against
// the wrong tenant, where this single-tenant app doesn't exist (AADSTS700016).
const getGraphToken = async (loginHint: string): Promise<string> => {
  const instance = await initMsal();
  const request = { scopes: GRAPH_SCOPES, loginHint };
  try {
    return (await instance.acquireTokenSilent(request)).accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      return (await instance.acquireTokenPopup(request)).accessToken;
    }
    throw error;
  }
};

// One conversation message, normalised to the same shape logEmail consumes so
// the native write path can be reused per message.
export type ThreadMessage = {
  subject: string;
  body: string;
  fromEmail: string;
  fromName: string;
  participants: EmailParticipant[];
  sentAt: string | null;
  internetMessageId: string;
  inReplyTo: string;
  references: string[];
  direction: EmailDirection;
};

type GraphRecipient = { emailAddress?: { address?: string; name?: string } };
type GraphMessage = {
  subject?: string;
  body?: { content?: string };
  from?: GraphRecipient;
  toRecipients?: GraphRecipient[];
  ccRecipients?: GraphRecipient[];
  sentDateTime?: string;
  receivedDateTime?: string;
  internetMessageId?: string;
  internetMessageHeaders?: { name: string; value: string }[];
};

const recip = (r: GraphRecipient): { email: string; name: string } => ({
  email: r.emailAddress?.address ?? '',
  name: r.emailAddress?.name ?? '',
});

const headerValue = (
  headers: { name: string; value: string }[] | undefined,
  name: string,
): string =>
  headers?.find((h) => h.name.toLowerCase() === name.toLowerCase())?.value ?? '';

const extractMessageIds = (value: string): string[] => value.match(/<[^>]+>/g) ?? [];

const mapGraphMessage = (m: GraphMessage, mailboxOwner: string): ThreadMessage => {
  const from = recip(m.from ?? {});
  const to = (m.toRecipients ?? []).map(recip).filter((r) => r.email);
  const cc = (m.ccRecipients ?? []).map(recip).filter((r) => r.email);
  const participants: EmailParticipant[] = [
    ...(from.email ? [{ email: from.email, name: from.name, role: 'FROM' as const }] : []),
    ...to.map((r) => ({ email: r.email, name: r.name, role: 'TO' as const })),
    ...cc.map((r) => ({ email: r.email, name: r.name, role: 'CC' as const })),
  ];
  const direction: EmailDirection =
    from.email.toLowerCase() === mailboxOwner.toLowerCase() ? 'OUTBOUND' : 'INBOUND';
  return {
    subject: m.subject || '(no subject)',
    body: m.body?.content ?? '',
    fromEmail: from.email,
    fromName: from.name,
    participants,
    sentAt: m.sentDateTime ?? m.receivedDateTime ?? null,
    internetMessageId: m.internetMessageId ?? '',
    inReplyTo: extractMessageIds(headerValue(m.internetMessageHeaders, 'In-Reply-To'))[0] ?? '',
    references: extractMessageIds(headerValue(m.internetMessageHeaders, 'References')),
    direction,
  };
};

// Fetch every message of the open conversation that lives in the user's mailbox
// (inbox + sent + folders), oldest first. Body is requested as plain text.
export const fetchConversation = async (
  conversationId: string,
  mailboxOwner: string,
): Promise<ThreadMessage[]> => {
  if (!conversationId) return [];
  // Use the CLS mailbox owner as the login hint so MSAL targets the right
  // account/tenant (the add-in is gated to the clswind mailbox).
  const token = await getGraphToken(mailboxOwner);
  const select =
    'subject,body,from,toRecipients,ccRecipients,sentDateTime,receivedDateTime,internetMessageId,internetMessageHeaders';
  // NOTE: no $orderby. Graph rejects filter-by-conversationId combined with
  // sort-by-receivedDateTime ("InefficientFilter"), so we sort client-side.
  // The conversationId from Office.js is base64-ish (+/=) - it MUST be
  // URL-encoded or the '+' becomes a space and Graph reports a malformed id.
  const filter = encodeURIComponent(`conversationId eq '${conversationId}'`);
  let next =
    `https://graph.microsoft.com/v1.0/me/messages` +
    `?$filter=${filter}` +
    `&$select=${select}&$top=50`;
  const out: ThreadMessage[] = [];
  while (next) {
    const res = await fetch(next, {
      headers: {
        Authorization: `Bearer ${token}`,
        // Plain-text bodies, matching the single-email logging path.
        Prefer: 'outlook.body-content-type="text"',
      },
    });
    if (!res.ok) {
      throw new Error(`Microsoft Graph error ${res.status}: ${await res.text()}`);
    }
    const data = (await res.json()) as { value: GraphMessage[]; '@odata.nextLink'?: string };
    for (const m of data.value) out.push(mapGraphMessage(m, mailboxOwner));
    next = data['@odata.nextLink'] ?? '';
  }
  // Oldest-first so the thread reads naturally and replies attach after parents.
  out.sort((a, b) => (a.sentAt ?? '').localeCompare(b.sentAt ?? ''));
  return out;
};
